import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';

export type AccountUser = {
  email: string;
  entityName: string;
  entityRef: string;
  displayName: string;
  mustChangePassword: boolean;
  isAdmin: boolean;
  disabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type MeResponse = {
  email: string;
  entityRef: string;
  displayName: string;
  mustChangePassword: boolean;
  isAdmin: boolean;
};

export interface AccountsApi {
  me(): Promise<MeResponse>;
  listUsers(): Promise<AccountUser[]>;
  createUser(input: {
    email: string;
    displayName: string;
    isAdmin: boolean;
  }): Promise<{ user: AccountUser; tempPassword: string }>;
  resetPassword(
    email: string,
  ): Promise<{ user: AccountUser; tempPassword: string }>;
  setDisabled(email: string, disabled: boolean): Promise<AccountUser>;
  setAdmin(email: string, isAdmin: boolean): Promise<AccountUser>;
  changePassword(input: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void>;
}

export const accountsApiRef = createApiRef<AccountsApi>({
  id: 'plugin.accounts.service',
});

export class AccountsClient implements AccountsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl() {
    return this.discoveryApi.getBaseUrl('accounts');
  }

  private async json<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(`${base}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed (${res.status})`);
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return res.json();
  }

  me() {
    return this.json<MeResponse>('/me');
  }

  async listUsers() {
    const data = await this.json<{ users: AccountUser[] }>('/users');
    return data.users;
  }

  createUser(input: { email: string; displayName: string; isAdmin: boolean }) {
    return this.json<{ user: AccountUser; tempPassword: string }>('/users', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  resetPassword(email: string) {
    return this.json<{ user: AccountUser; tempPassword: string }>(
      `/users/${encodeURIComponent(email)}/reset-password`,
      { method: 'POST', body: '{}' },
    );
  }

  async setDisabled(email: string, disabled: boolean) {
    const data = await this.json<{ user: AccountUser }>(
      `/users/${encodeURIComponent(email)}/disable`,
      {
        method: 'POST',
        body: JSON.stringify({ disabled }),
      },
    );
    return data.user;
  }

  async setAdmin(email: string, isAdmin: boolean) {
    const data = await this.json<{ user: AccountUser }>(
      `/users/${encodeURIComponent(email)}/admin`,
      {
        method: 'POST',
        body: JSON.stringify({ isAdmin }),
      },
    );
    return data.user;
  }

  async changePassword(input: {
    currentPassword: string;
    newPassword: string;
  }) {
    await this.json('/change-password', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }
}
